import { useCallback, useEffect, useMemo, useState } from "react";
import { useTheme, Grid, Button, TextField, Typography } from "@mui/material";
import { useIntl } from "react-intl";
import { isEqual } from "lodash-es";

import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import AddIcon from "@mui/icons-material/Add";

import TitledCard from "../../TitledCard";
import useEditedStyles, { useEditedColor } from "../../../../../hooks/useEditedStyles";

import { ReactComponent as RemotecontrolImage } from "./remotecontrol.svg";
import ButtonSettings from "./ButtonSettings";
import { ButtonSettingsValue, DisplayZoneValue, Partition, RemotecontrolValue, SmartalarmUser } from "../types";
import MacItem, { MacItemValue } from "./MacItem";
import { uuidv4 } from "../../../../../utils/utils";
import { ResetAdornment, useResetContextMenu } from "../../../../../utils/ResetContextMenuProvider";

interface RemotecontrolProps {
  index: number;
  value: RemotecontrolValue;
  saved: RemotecontrolValue | null;
  zones: DisplayZoneValue[];
  partitions: Partition[];
  users: SmartalarmUser[];
  onChange: (index: number, value: RemotecontrolValue) => void;
  disabled?: boolean;
}

const Remotecontrol = ({ index, value, saved, zones, partitions, users, onChange, disabled }: RemotecontrolProps) => {
  const [name, setName] = useState(value.name || "");
  const [phisycals, setPhisycals] = useState<MacItemValue[] | null>(null);
  const [buttonValues, setButtonValues] = useState<ButtonSettingsValue[] | null>(null);
  // const [newItem, setNewItem] = useState(false);

  const { formatMessage } = useIntl();
  const theme = useTheme();
  const editedStyle = useEditedStyles();
  const { background: editedBackground, text: editedText } = useEditedColor();
  const { openContextMenu } = useResetContextMenu();

  const onDelete = useCallback(() => {
    value.deleted = true;
    onChange(index, value);
  }, [index, value, onChange]);

  const onRestore = useCallback(() => {
    delete value.deleted;
    onChange(index, value);
  }, [index, value, onChange]);

  useEffect(() => {
    // if (value.newItem) {
    //   // setNewItem(true);
    //   delete value.newItem;
    // }
    setName(value.name || "");
    setButtonValues((old) => {
      const values = value.buttons?.map((button) => button || { function: "NULL" }) || [
        { function: "NULL" },
        { function: "NULL" },
        { function: "NULL" },
        { function: "NULL" },
      ];
      return isEqual(old, values) ? old : values;
    });
    setPhisycals((old) => {
      return isEqual(old, value.physical) ? old : value.physical;
    });
  }, [value]);

  // const overview = useMemo(() => {
  //   if (!phisycals || phisycals.length === 0) {
  //     return null;
  //   }
  //   return (
  //     <Grid container spacing={0.5} sx={{ padding: 1 }}>
  //       {phisycals.length === value.physical.length &&
  //         value.physical
  //           .filter((phisycal) => phisycal.mac || phisycal.user)
  //           .map((phisycal) => {
  //             const user = users.find((u) => u.uuid === phisycal.user);
  //             const userLabel = user ? [user?.name, user.deleted && ` (${formatMessage({ defaultMessage: "deleted" })})`].filter((m) => !!m).join(" ") : "";
  //             return (
  //               <Grid item key={`mac-${phisycal.uuid}`}>
  //                 <HTChip
  //                   label={[phisycal.deleted && `${formatMessage({ defaultMessage: "deleted" }).toUpperCase()} |`, userLabel].filter((m) => !!m).join(" ")}
  //                   label2={[phisycal.deleted && `${formatMessage({ defaultMessage: "deleted" }).toUpperCase()} |`, phisycal.mac].filter((m) => !!m).join(" ")}
  //                   code={user?.deleted ? undefined : user?.code}
  //                   icon={user?.deleted ? <PersonOffIcon /> : <SettingsRemoteOutlinedIcon />}
  //                 />
  //               </Grid>
  //             );
  //           })}
  //     </Grid>
  //   );
  // }, [phisycals, value, users, formatMessage]);

  const setButtonValue = useCallback(
    (nr: number, v: ButtonSettingsValue) => {
      if (!v || !buttonValues) {
        return;
      }
      value.buttons[nr - 1] = v;
      buttonValues[nr - 1] = v;
      value.buttons.splice(4);
      onChange(index, value);
    },
    [value, index, buttonValues, onChange]
  );

  const addNewPhysical = useCallback(() => {
    if (!value) {
      return;
    }
    if (!value.physical) {
      value.physical = [];
    }
    value.physical.push({ uuid: uuidv4(), user: "", mac: "" });
    setPhisycals([...value.physical]);
  }, [value]);

  const buttons = useMemo(
    () =>
      buttonValues ? (
        <Grid container spacing={1} sx={{ paddingLeft: 1 }} flexDirection="column">
          <ButtonSettings
            nr={1}
            partitions={partitions}
            zones={zones}
            value={buttonValues[0]}
            saved={saved?.buttons[0] || null}
            onChange={setButtonValue}
            disabled={disabled || value.deleted}
          />
          <ButtonSettings
            nr={2}
            partitions={partitions}
            zones={zones}
            value={buttonValues[1]}
            saved={saved?.buttons[1] || null}
            onChange={setButtonValue}
            disabled={disabled || value.deleted}
          />
          <ButtonSettings
            nr={3}
            partitions={partitions}
            zones={zones}
            value={buttonValues[2]}
            saved={saved?.buttons[2] || null}
            onChange={setButtonValue}
            disabled={disabled || value.deleted}
          />
          <ButtonSettings
            nr={4}
            partitions={partitions}
            zones={zones}
            value={buttonValues[3]}
            saved={saved?.buttons[3] || null}
            onChange={setButtonValue}
            disabled={disabled || value.deleted}
          />
        </Grid>
      ) : null,
    [value, buttonValues, setButtonValue, partitions, zones, saved, disabled]
  );

  const physicalRemotes = useMemo(() => {
    if (!value) {
      return null;
    }
    return (
      <Grid container spacing={1} flexDirection="column">
        {phisycals?.length === value.physical.length &&
          value.physical.map((mac, i) => (
            <MacItem
              key={`p-${mac.uuid}`}
              value={value.physical[i]}
              saved={saved?.physical?.find((p) => isEqual(p.uuid, value.physical[i].uuid)) || null}
              users={users}
              onChange={(v: MacItemValue) => {
                value.physical[i] = v;
                onChange(index, value);
              }}
              disabled={disabled || value.deleted}
            />
          ))}
        <Grid item>
          <Button startIcon={<AddIcon />} onClick={() => addNewPhysical()} disabled={disabled || value.deleted}>
            {formatMessage({ defaultMessage: "New physical remote" })}
          </Button>
        </Grid>
      </Grid>
    );
  }, [phisycals, value, saved, users, index, disabled, onChange, addNewPhysical, formatMessage]);

  const editView = useMemo(() => {
    if (!value || !phisycals) {
      return null;
    }
    return (
      <>
        <Grid container sx={{ flexDirection: { xs: "column", sm: "row-reverse" }, justifyContent: { sm: "space-between" } }}>
          {!value.deleted && !disabled && (
            <Grid item sx={{ alignSelf: { xs: "flex-end", sm: "flex-start" } }}>
              <Button startIcon={<DeleteForeverOutlinedIcon />} color="error" onClick={onDelete} sx={{ m: 1 }}>
                {formatMessage({ defaultMessage: "Remove" })}
              </Button>
            </Grid>
          )}
          {value.deleted && !disabled && (
            <Grid item sx={{ alignSelf: { xs: "flex-end", sm: "flex-start" } }}>
              <Button color="inherit" onClick={onRestore} sx={{ m: 1 }}>
                {formatMessage({ defaultMessage: "Restore" })}
              </Button>
            </Grid>
          )}
          <Grid item xs={12} sm="auto">
            <Grid container spacing={1} sx={{ padding: 1 }} flexDirection="row">
              <Grid item xs={12} sm="auto">
                <TextField
                  label={formatMessage({ defaultMessage: "Name" })}
                  variant="filled"
                  value={name}
                  className={saved?.name !== name ? editedStyle.input : ""}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={() => {
                    value.name = name;
                    onChange(index, value);
                  }}
                  sx={{ margin: 0, minWidth: { sm: 180 }, width: { sm: 180 } }}
                  size="small"
                  autoComplete="off"
                  disabled={disabled || value.deleted}
                  fullWidth
                  InputProps={saved && saved.name !== name ? { endAdornment: <ResetAdornment /> } : undefined}
                  onContextMenu={saved && saved.name !== name ? (e) => openContextMenu(e, `smartalarm.remotecontrol.${index}.name`) : undefined}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid
          container
          spacing={1}
          flexDirection="column"
          sx={{ padding: 1, paddingBottom: 0, [theme.breakpoints.up("sm")]: { flexDirection: "row" } }}
          alignItems="center"
        >
          <Grid item>
            <RemotecontrolImage height={250} />
          </Grid>
          <Grid item flex={1}>
            {buttons}
          </Grid>
        </Grid>
        <Grid container spacing={1} sx={{ padding: 1 }} flexDirection="column">
          <Grid item>
            <Typography variant="subtitle2">{formatMessage({ defaultMessage: "Physical remotes" })}</Typography>
          </Grid>
          <Grid item>{physicalRemotes}</Grid>
        </Grid>
      </>
    );
  }, [value, phisycals, disabled, onDelete, formatMessage, onRestore, name, saved, editedStyle.input, theme.breakpoints, buttons, physicalRemotes, onChange, index, openContextMenu]);

  return (
    <>
      <TitledCard
        color={value.deleted ? editedBackground : undefined}
        contrastColor={value.deleted ? editedText : undefined}
        title={
          (value.deleted ? `${formatMessage({ defaultMessage: "deleted" }).toUpperCase()} | ` : "") + formatMessage({ defaultMessage: "Remote control config" })
        }
        // modes={[
        // {
        //   icon: <RemoveRedEyeOutlinedIcon fontSize="small" />,
        //   content: overview,
        //   title:
        //     (value.deleted ? `${formatMessage({ defaultMessage: "deleted" }).toUpperCase()} | ` : "") +
        //     formatMessage({ defaultMessage: "Remote control config" }) +
        //     (name ? ` | ${name}` : ""),
        // },
        //   {
        //     icon:
        //       disabled || value.deleted ? (
        //         <Badge
        //           badgeContent={<RemoveRedEyeOutlinedIcon fontSize="inherit" />}
        //           anchorOrigin={{
        //             vertical: "bottom",
        //             horizontal: "right",
        //           }}
        //         >
        //           <EditOutlinedIcon fontSize="small" />
        //         </Badge>
        //       ) : (
        //         <EditOutlinedIcon fontSize="small" />
        //       ),
        //     content: editView,
        //     title:
        //       (value.deleted ? `${formatMessage({ defaultMessage: "deleted" }).toUpperCase()} | ` : "") +
        //       formatMessage({ defaultMessage: "Remote control config" }),
        //   },
        // ]}
        // activeMode={newItem ? 1 : 0}
      >
        {editView}
      </TitledCard>
    </>
  );
};

export default Remotecontrol;
